import styled, { keyframes, css } from 'styled-components';

import { ACQ, ACQ_MOBILE, ANIM, COLORS } from '../../../../theme/tokens';
import { hoverWash } from '../../../../theme/mixins';
import { Text } from '../../../../components';
// The promotions browser borrows the acquisition Campaigns CARD atoms (period, thumb,
// title, chevron). Its browser CHROME is shared too, from CampaignBrowser.styles beside
// them: both browsers group identically, so a retune there lands on both. What stays
// local here is this browser's own list: a single column of full-width cards carrying
// the activation benefits, where the acquisition one is a two-column grid of countdown
// cards.
import {
  Chev,
  LoadMore,
  Title,
  JustActivated,
  activatedHueCss,
} from '../../../acquisition/components/Campaigns/Campaigns.styles';

// Re-exported so the Promotions component imports the tagline from its own
// styles module (it renders identically to the acquisition card's tagline).
export { JustActivated };

// --- Card list (single column; each card is a full-width two-panel row) ------
export const PromoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 6px 10px;
`;

const cardIn = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

// Desktop: identity on the left (the "period • status" header, then the
// thumb/title/chevron row) with the activation benefits flush to the right.
// Mobile: the two zones stack.
export const PromoCard = styled.div<{ $clickable: boolean; $justActivated?: boolean }>`
  display: flex;
  align-items: center;
  gap: 18px;
  background: ${ACQ.campCard};
  border-radius: 16px;
  padding: 20px;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  animation: ${cardIn} ${ANIM.PROMO_CARD_ENTER_MS}ms ${ACQ.ease} both;
  ${(p) =>
    p.$clickable
      ? css`
          ${hoverWash}
          &:hover ${Chev} {
            transform: translateX(2px);
          }
        `
      : ''}

  ${ACQ_MOBILE} {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  /* Overrides the entrance animation with the one-shot green hue when this is the
     promotion the owner just activated (entrance already played on the redirect). */
  ${(p) => (p.$justActivated ? activatedHueCss : '')}
`;

export const CardLeft = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// "Campaign period: … • Paused on …" — the status follows the period inline,
// bullet-separated; the whole line wraps on a narrow card.
export const CardHead = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

// Bullet + status, kept together as one wrap unit after the period.
export const StatusGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const Bullet = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${ACQ.t500};
  flex-shrink: 0;
`;

// The acquisition Title flex-grows to fill its row; here the chevron should sit
// directly after the title text, so size the title to its content (shrinking +
// clamping to 2 lines only when the row would overflow).
export const CardTitle = styled(Title)`
  flex: 0 1 auto;
`;

// Wraps the title so the "Just activated" tagline can sit directly beneath it,
// keeping the chevron tucked right after the title (the title hugs its content,
// like CardTitle, rather than filling the row).
export const PromoTitleBox = styled.div`
  flex: 0 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

export const StatusInline = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`;

// Activation benefits, flush to the right of the card.
export const CardRight = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;

  ${ACQ_MOBILE} {
    width: 100%;
  }
`;

// "ACTIVATION BENEFITS" — a centred 296px block, the uppercase label flanked by
// solid hairlines (Figma 6466:38081).
export const BenefitsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 296px;
  max-width: 100%;
`;

export const HeaderLine = styled.div`
  flex: 1 1 0;
  min-width: 1px;
  height: 1px;
  background: ${ACQ.divider};
`;

export const HeaderLabel = styled(Text)`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${COLORS.unlockLabel};
`;

// The three +N benefit stats, centred on desktop, spread to the card edges when
// it stacks on mobile.
export const BenefitsRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 20px;

  ${ACQ_MOBILE} {
    width: 100%;
    justify-content: space-between;
    gap: 12px;
  }
`;

export const Benefit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

export const BenefitValue = styled(Text)`
  font-size: 24px;
  font-weight: 400;
  line-height: 1.1;
  color: ${COLORS.benefitPositive};
`;

export const BenefitLabel = styled(Text)`
  width: 70px;
  font-size: 14px;
  font-weight: 300;
  line-height: 1.25;
  text-align: center;
  color: ${ACQ.t400};
`;

// Paused/ended status, rendered inline after the period ("• Paused on …").
export const StatusLabel = styled(Text)`
  font-size: 12px;
  font-weight: 300;
  color: ${ACQ.t400};
  white-space: nowrap;
`;

export const StatusTime = styled(Text)`
  font-size: 12px;
  font-weight: 500;
  color: ${ACQ.t300};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

// The acquisition "Load more" button is a grid child (grid-column: 1 / -1);
// reused here inside the flex PromoList it stretches to full width anyway, but
// pin it explicitly so the intent is clear regardless of the parent's layout.
export const MoreButton = styled(LoadMore)`
  width: 100%;
`;
